import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAccentColor } from '../lib/jlfcTheme';
import './JlfcPaginatedTable.css';
import {
  Paper,
  Table,
  Text,
  Loader,
  Center,
  Group,
  Pagination,
  Select,
  Button,
  Box,
  Stack,
  Divider,
  Checkbox,
  Collapse,
  Badge,
  UnstyledButton,
} from '@mantine/core';
import { IconSearch, IconFilterOff, IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import SearchFilterInput from './SearchFilterInput';
import { PAGE_SIZE_OPTIONS, useTableListState } from '../hooks/useTableListState';
import {
  activeFilterStyles,
  FILTER_WIDTH_STATUT,
  resolveFilterWidth,
} from '../lib/tableFilterStyles';

const ACTIF_FILTER_DATA = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'active', label: 'Actifs uniquement' },
  { value: 'inactive', label: 'Inactifs uniquement' },
];

function CriteriaFilterSelect({ filter, isActive, ...selectProps }) {
  const width = resolveFilterWidth(filter);
  return (
    <Box style={{ width, flexShrink: 0 }}>
      <Select
        {...selectProps}
        size="sm"
        styles={activeFilterStyles(isActive)}
        style={{ width: '100%' }}
      />
    </Box>
  );
}

/**
 * Tableau avec barre de recherche, filtres, tri sur colonnes et pagination.
 */
export default function JlfcPaginatedTable({
  records,
  loading,
  columns,
  filters = [],
  searchPlaceholder = 'Rechercher…',
  searchName = 'jlfc-table-search',
  searchAriaLabel = 'Rechercher dans le tableau',
  showActifFilter = true,
  actifFilterWidth = FILTER_WIDTH_STATUT,
  defaultPageSize = 15,
  defaultSortBy,
  defaultSortDir = 'asc',
  recordPredicate,
  initialColumnFilters,
  initialQuery,
  initialPage,
  initialPageSize,
  initialSortBy,
  initialSortDir,
  onPersistedStateChange,
  extraHasActiveFilters = false,
  extraActiveCriteriaCount = 0,
  onResetExtra,
  extraCriteriaLead = null,
  extraCriteria = null,
  extraToolbar = null,
  belowFilters = null,
  emptyText = 'Aucun enregistrement.',
  emptyFilteredText = 'Aucun résultat pour ces critères.',
  toolbarRight = null,
  renderActions,
  actionColumnWidth = 80,
  selectedIds = null,
  onToggleSelect = null,
  isRowSelectable = null,
  tableSummary = null,
  compact = false,
  onVisibleRowsChange = null,
  onRowClick = null,
  expandedRowId = null,
  renderExpandedRow = null,
  headerDividers = false,
  tableLayout = 'auto',
}) {
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const hasSelection = typeof onToggleSelect === 'function';
  const table = useTableListState(records, {
    columns,
    filters,
    defaultPageSize,
    defaultSortBy,
    defaultSortDir,
    recordPredicate,
    initialColumnFilters,
    initialQuery,
    initialPage,
    initialPageSize,
    initialSortBy,
    initialSortDir,
    onPersistedStateChange,
  });
  const {
    query,
    setQuery,
    actifFilter,
    setActifFilter,
    columnFilters,
    setColumnFilter,
    filterSelectData,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortBy,
    sortDir,
    toggleSort,
    paginated,
    total,
    totalPages,
    from,
    to,
    resetFilters,
    hasActiveFilters,
    filtered,
  } = table;

  useEffect(() => {
    if (typeof onVisibleRowsChange !== 'function') return;
    onVisibleRowsChange(paginated, { filtered, page, pageSize, total, from, to });
  }, [onVisibleRowsChange, paginated, filtered, page, pageSize, total, from, to]);

  const expandedPageJump = useRef(null);
  useEffect(() => {
    if (expandedRowId == null) {
      expandedPageJump.current = null;
      return;
    }
    if (expandedPageJump.current === String(expandedRowId)) return;
    const index = filtered.findIndex((row) => String(row.id) === String(expandedRowId));
    if (index < 0) return;
    expandedPageJump.current = String(expandedRowId);
    const targetPage = Math.floor(index / pageSize) + 1;
    if (targetPage !== page) setPage(targetPage);
  }, [expandedRowId, filtered, pageSize, page, setPage]);

  const summaryNode = tableSummary ? tableSummary(filtered) : null;
  const hasSummaryBar = Boolean(summaryNode);

  const sortIcon = (key) => {
    if (sortBy !== key) return null;
    return sortDir === 'asc' ? (
      <IconChevronUp size={14} style={{ verticalAlign: 'middle' }} />
    ) : (
      <IconChevronDown size={14} style={{ verticalAlign: 'middle' }} />
    );
  };

  const showFilterPanel =
    showActifFilter ||
    filters.length > 0 ||
    extraCriteriaLead ||
    extraCriteria ||
    extraToolbar ||
    hasActiveFilters ||
    extraHasActiveFilters;

  const showResetButton = hasActiveFilters || extraHasActiveFilters;

  const activeCriteriaCount = useMemo(() => {
    let count = Object.values(columnFilters).filter(Boolean).length;
    if (actifFilter !== 'all') count += 1;
    return count;
  }, [columnFilters, actifFilter]);

  const totalActiveCriteria = activeCriteriaCount + extraActiveCriteriaCount;

  const hasAnyCriteriaActive = totalActiveCriteria > 0 || extraHasActiveFilters;

  const handleResetAll = () => {
    resetFilters();
    onResetExtra?.();
  };

  return (
    <Stack
      gap="md"
      style={
        compact
          ? { display: 'flex', flexDirection: 'column', width: '100%' }
          : {
              flex: 1,
              minHeight: 0,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }
      }
    >
      <Box style={{ flexShrink: 0, width: '100%', display: 'flex', flexDirection: 'column', gap: 'var(--mantine-spacing-md)' }}>
        <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
          <SearchFilterInput
            placeholder={searchPlaceholder}
            name={searchName}
            aria-label={searchAriaLabel}
            leftSection={<IconSearch size={16} />}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 220, maxWidth: 420 }}
          />
          {toolbarRight}
        </Group>

        {showFilterPanel ? (
        <Paper withBorder radius="md" p="md" bg="var(--mantine-color-gray-0)">
          <Group justify="space-between" align="center" wrap="nowrap" gap="sm" mb={filtersExpanded ? 'sm' : 0}>
            <UnstyledButton
              onClick={() => setFiltersExpanded((open) => !open)}
              aria-expanded={filtersExpanded}
              aria-label={filtersExpanded ? 'Replier les critères' : 'Déplier les critères'}
              style={{ flex: 1, minWidth: 0 }}
            >
              <Group gap="xs" wrap="nowrap">
                {filtersExpanded ? (
                  <IconChevronUp size={16} style={{ flexShrink: 0 }} />
                ) : (
                  <IconChevronDown size={16} style={{ flexShrink: 0 }} />
                )}
                <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                  Critères
                </Text>
                {hasAnyCriteriaActive ? (
                  <Badge size="sm" variant="light" color={getAccentColor()}>
                    {totalActiveCriteria || (extraHasActiveFilters ? 1 : 0)}
                    {' '}
                    actif{(totalActiveCriteria || (extraHasActiveFilters ? 1 : 0)) > 1 ? 's' : ''}
                  </Badge>
                ) : (
                  <Text size="xs" c="dimmed">
                    Aucun filtre actif
                  </Text>
                )}
              </Group>
            </UnstyledButton>
            {showResetButton ? (
              <Button
                variant="light"
                color="gray"
                size="sm"
                leftSection={<IconFilterOff size={16} />}
                onClick={handleResetAll}
                style={{ flexShrink: 0 }}
              >
                Réinitialiser les filtres
              </Button>
            ) : null}
          </Group>

          <Collapse expanded={filtersExpanded}>
            <Stack gap="sm">
              {showActifFilter || filters.length > 0 || extraCriteriaLead || extraCriteria ? (
                <Group align="flex-end" gap="sm" wrap="wrap">
                  {showActifFilter ? (
                    <Box style={{ width: actifFilterWidth, flexShrink: 0 }}>
                      <Select
                        label="Statut"
                        data={ACTIF_FILTER_DATA}
                        value={actifFilter}
                        onChange={(v) => setActifFilter(v || 'all')}
                        size="sm"
                        allowDeselect={false}
                        styles={activeFilterStyles(actifFilter !== 'all')}
                        style={{ width: '100%' }}
                      />
                    </Box>
                  ) : null}
                  {filters.map((f) => {
                    const value = columnFilters[f.key] || null;
                    return (
                      <CriteriaFilterSelect
                        key={f.key}
                        filter={f}
                        isActive={Boolean(value)}
                        label={f.label}
                        placeholder="Tous"
                        data={filterSelectData[f.key] || []}
                        value={value}
                        onChange={(v) => setColumnFilter(f.key, v)}
                        clearable
                        searchable
                      />
                    );
                  })}
                  {extraCriteriaLead}
                  {extraCriteria}
                </Group>
              ) : null}

              {extraToolbar ? (
                <>
                  {showActifFilter || filters.length > 0 || extraCriteriaLead || extraCriteria ? (
                    <Divider />
                  ) : null}
                  {extraToolbar}
                </>
              ) : null}
            </Stack>
          </Collapse>
        </Paper>
        ) : null}

        {belowFilters}
      </Box>

      <Paper
        withBorder
        radius="md"
        p={0}
        bg={hasSummaryBar ? 'var(--mantine-color-gray-0)' : undefined}
        style={
          compact
            ? { display: 'flex', flexDirection: 'column', gap: 0, width: '100%' }
            : {
                flex: 1,
                minHeight: 0,
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
              }
        }
      >
        {loading ? (
          <Center py="xl">
            <Loader color={getAccentColor()} />
          </Center>
        ) : total === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            {hasActiveFilters || extraHasActiveFilters ? emptyFilteredText : emptyText}
          </Text>
        ) : (
          <>
            {hasSummaryBar ? (
              <Box className="jlfc-paginated-table-summary">
                {summaryNode}
              </Box>
            ) : null}
            <Box
              className={`jlfc-paginated-table-scroll${
                hasSummaryBar ? ' jlfc-paginated-table-scroll--with-summary' : ''
              }`}
              style={compact ? undefined : { flex: 1, minHeight: 0 }}
            >
              <Table striped highlightOnHover stickyHeader layout={tableLayout} style={{ width: '100%' }}>
                <Table.Thead>
                <Table.Tr>
                  {hasSelection ? <Table.Th w={40} /> : null}
                  {columns.map((col, index) => (
                    <Table.Th
                      key={col.key}
                      w={col.width}
                      ta={col.align}
                      onClick={col.sortable !== false ? () => toggleSort(col.key) : undefined}
                      style={{
                        ...(col.sortable !== false ? { cursor: 'pointer', userSelect: 'none' } : null),
                        ...(col.width ? { whiteSpace: 'nowrap' } : null),
                        ...(headerDividers && (index > 0 || hasSelection)
                          ? { borderLeft: '1px solid var(--table-border-color, var(--mantine-color-gray-3))' }
                          : null),
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start',
                          gap: 2,
                          width: '100%',
                        }}
                      >
                        {col.align === 'right' && col.sortable !== false ? sortIcon(col.key) : null}
                        {col.label}
                        {col.align !== 'right' && col.sortable !== false ? sortIcon(col.key) : null}
                      </span>
                    </Table.Th>
                  ))}
                  {renderActions ? (
                    <Table.Th
                      w={actionColumnWidth}
                      style={headerDividers ? { borderLeft: '1px solid var(--table-border-color, var(--mantine-color-gray-3))' } : undefined}
                    >
                      Actions
                    </Table.Th>
                  ) : null}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {paginated.map((row) => {
                  const selectable = !isRowSelectable || isRowSelectable(row);
                  const checked = selectedIds?.includes(row.id) ?? false;
                  const isExpanded = expandedRowId != null && String(expandedRowId) === String(row.id);
                  const colSpan = columns.length + (hasSelection ? 1 : 0) + (renderActions ? 1 : 0);
                  return (
                  <React.Fragment key={row.id}>
                  <Table.Tr
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    style={{
                      ...(onRowClick ? { cursor: 'pointer' } : null),
                      ...(isExpanded ? { boxShadow: 'inset 3px 0 0 var(--mantine-color-blue-filled)' } : null),
                    }}
                    data-expanded={isExpanded || undefined}
                  >
                    {hasSelection ? (
                      <Table.Td>
                        <Checkbox
                          checked={checked}
                          disabled={!selectable}
                          onChange={() => onToggleSelect(row)}
                          aria-label={`Sélectionner le contrôle ${row.id}`}
                        />
                      </Table.Td>
                    ) : null}
                    {columns.map((col, idx) => (
                      <Table.Td key={col.key} ta={col.align}>
                        {idx === 0 ? (
                          col.render ? (
                            col.render(row)
                          ) : (
                            <Text fw={500} size="sm">
                              {row[col.key] ?? '—'}
                            </Text>
                          )
                        ) : col.render ? (
                          col.render(row)
                        ) : (
                          row[col.key] ?? '—'
                        )}
                      </Table.Td>
                    ))}
                    {renderActions ? (
                      <Table.Td className="jlfc-table-actions-cell">{renderActions(row)}</Table.Td>
                    ) : null}
                  </Table.Tr>
                  {isExpanded && renderExpandedRow ? (
                    <Table.Tr>
                      <Table.Td
                        colSpan={colSpan}
                        p={0}
                        style={{ background: 'var(--mantine-color-gray-0)' }}
                      >
                        <Box p="md">{renderExpandedRow(row)}</Box>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                  </React.Fragment>
                  );
                })}
              </Table.Tbody>
            </Table>
            </Box>

            <Box
              px="md"
              py="sm"
              style={{
                flexShrink: 0,
                borderTop: '1px solid var(--mantine-color-gray-3)',
                background: 'var(--mantine-color-gray-0)',
              }}
            >
              <Group justify="space-between" align="center" wrap="wrap" gap="md">
                <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {from}-{to} sur {total}
                </Text>
                <Group gap="md" align="center" wrap="nowrap">
                  <Group gap={6} align="center" wrap="nowrap">
                    <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                      Par page
                    </Text>
                    <Select
                      size="xs"
                      w={72}
                      aria-label="Éléments par page"
                      data={PAGE_SIZE_OPTIONS.map((n) => ({
                        value: String(n),
                        label: String(n),
                      }))}
                      value={String(pageSize)}
                      onChange={(v) => setPageSize(parseInt(v, 10) || 15)}
                      allowDeselect={false}
                      comboboxProps={{ withinPortal: true }}
                    />
                  </Group>
                  <Pagination
                    value={page}
                    onChange={setPage}
                    total={totalPages}
                    size="sm"
                  />
                </Group>
              </Group>
            </Box>
          </>
        )}
      </Paper>
    </Stack>
  );
}
